import React, { useEffect, useRef, useState } from 'react';
import { m } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { SectionTag } from '../ui/SectionTag';
import { slides, highlights } from '../../data/insideLook';
export function InsideLook() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const goToIndex = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(index, slides.length - 1));
    const card = track.children[clamped] as HTMLElement | undefined;
    if (card) {
      track.scrollTo({
        left: card.offsetLeft - track.offsetLeft,
        behavior: 'smooth'
      });
    }
  };
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame: number;
    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const cards = Array.from(track.children) as HTMLElement[];
        let closest = 0;
        let minDistance = Infinity;
        cards.forEach((card, i) => {
          const distance = Math.abs(card.offsetLeft - track.offsetLeft - track.scrollLeft);
          if (distance < minDistance) {
            minDistance = distance;
            closest = i;
          }
        });
        setActiveIndex(closest);
      });
    };
    track.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      track.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(frame);
    };
  }, []);
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === slides.length - 1;
  return <section className="bg-white py-14 sm:py-20" data-id="element-100" data-mp="lecm15p0fn0w">
      <div className="mx-auto max-w-2xl px-4 text-center sm:px-6" data-id="element-101" data-mp="lg5vesr0clcl">
        <SectionTag icon="📸" tone="primary" data-id="element-102" data-mp="3l2w1l85zq038">
          Material real
        </SectionTag>
        <h2 className="mt-5 font-heading text-3xl font-bold leading-tight text-primary sm:text-4xl" data-id="element-103" data-mp="3p365mfa82gfg">
          Mira cómo prepararás tu próxima clase
        </h2>
        <p className="mt-4 text-base leading-relaxed text-ink sm:text-lg" data-id="element-104" data-mp="w74mhpzbqomh">
          Desliza y amplía estas seis páginas del pack: ejercicios explicados, clases organizadas y
          herramientas para adaptar y planificar.
        </p>
      </div>

      <div className="mx-auto mt-10 max-w-5xl px-4 sm:px-6" data-id="element-105" data-mp="10zo18q9qzpsv">
        <div ref={trackRef} className="scrollbar-hide flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-2 sm:gap-6" data-id="element-106" data-mp="112a9yt8dkbco">
          {slides.map(slide => <div key={slide.image} className="flex w-[82%] flex-shrink-0 snap-center flex-col sm:w-[46%]" data-id="element-107" data-mp="mb1moqry322p">
              <div className="flex items-center justify-center overflow-hidden rounded-2xl bg-paper p-3 shadow-card-light" data-id="element-108" data-mp="i9g5it8do1mp">
                <img src={slide.image} alt={slide.alt} loading="lazy" decoding="async" className="aspect-[500/707] w-full object-contain" data-id="element-109" data-mp="1xpf5hh7ucrbv" />
              </div>
              <div className="mt-3" data-id="element-110" data-mp="2677dt1ts0iam">
                <p className="font-heading text-sm font-semibold text-primary sm:text-base" data-id="element-111" data-mp="2688q19mj3atx">{slide.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-ink/80" data-id="element-112" data-mp="1rb88u01yqy9p">{slide.caption}</p>
                {slide.note && <p className="mt-1.5 text-xs leading-relaxed text-ink/50" data-id="element-113" data-mp="1w9syx6ni41kf">{slide.note}</p>}
              </div>
            </div>)}
        </div>

        <div className="mt-5 flex items-center justify-center gap-4" data-id="element-114" data-mp="2oxfd18ucuolj">
          <button type="button" onClick={() => goToIndex(activeIndex - 1)} disabled={isFirst} aria-label="Página anterior" className="flex h-10 w-10 items-center justify-center rounded-full border border-primary/15 text-primary transition-colors duration-150 hover:bg-primary/5 disabled:opacity-30 disabled:hover:bg-transparent" data-id="element-115" data-mp="2ozrqp9t2ycss">
            <ChevronLeftIcon size={18} data-id="element-116" data-mp="37q9inc6394d3" />
          </button>
          <span className="text-sm font-medium text-ink/60 sm:hidden" data-id="element-117" data-mp="1k0wgfvjdkaub">
            {activeIndex + 1}/{slides.length}
          </span>
          <button type="button" onClick={() => goToIndex(activeIndex + 1)} disabled={isLast} aria-label="Página siguiente" className="flex h-10 w-10 items-center justify-center rounded-full border border-primary/15 text-primary transition-colors duration-150 hover:bg-primary/5 disabled:opacity-30 disabled:hover:bg-transparent" data-id="element-118" data-mp="1f2v0vkl5qtnl">
            <ChevronRightIcon size={18} data-id="element-119" data-mp="1xtcoxbv1p12m" />
          </button>
        </div>

        <p className="mx-auto mt-6 max-w-2xl text-center text-sm leading-relaxed text-ink/70" data-id="element-120" data-mp="1sz8fmlyb4gyd">
          Estas son páginas del material que recibirás. Cada tomo combina fichas de consulta con clases
          organizadas y hojas imprimibles.
        </p>
      </div>

      <div className="mx-auto mt-14 max-w-3xl px-4 sm:px-6" data-id="element-121" data-mp="2gplzvqanhbie">
        <m.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45
      }} className="rounded-3xl bg-paper p-8 sm:p-12" data-id="element-122" data-mp="2go198brgicpt">
          <h3 className="font-heading text-2xl font-bold leading-snug text-primary sm:text-3xl" data-id="element-123" data-mp="2xz6oi28aj5tn">
            No es solo información, es una herramienta de precisión.
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-ink sm:text-base" data-id="element-124" data-mp="37xw1bglby031">
            Lo que ves arriba es exactamente lo que recibirás: fichas técnicas de alta definición diseñadas
            para ser consultadas en el fragor de la clase.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-ink sm:text-base" data-id="element-125" data-mp="3gqppy4mo2btk">
            Olvídate de los manuales densos con párrafos interminables. Aquí encontrarás:
          </p>

          <div className="mt-8 grid gap-5 sm:grid-cols-2" data-id="element-126" data-mp="3qporl390l772">
            {highlights.map(item => <div key={item.title} className="flex items-start gap-3" data-id="element-127" data-mp="3vnq8pimvqwfw">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-accent/10" data-id="element-128" data-mp="35y9toxibwugt">
                  <item.icon size={18} className="text-accent" data-id="element-129" data-mp="311axm9f3udfz" />
                </div>
                <div data-id="element-130" data-mp="3fp4hi6b8424d">
                  <p className="font-heading text-sm font-semibold text-primary" data-id="element-131" data-mp="3fqfhrw5gmrqq">{item.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-ink/80" data-id="element-132" data-mp="3awl171l5isvv">{item.text}</p>
                </div>
              </div>)}
          </div>
        </m.div>
      </div>
    </section>;
}